/**
 * Normalize ChPL content for consistent storage and matching.
 *
 * Applied during extraction before storing in the database.
 * Normalizes invisible characters, fancy quotes, dashes, etc.
 * Preserves markdown formatting (asterisks), newlines, and case.
 */
export function normalizeChplContent(text: string): string {
  return (
    text
      // Remove soft hyphens (invisible hyphenation hints)
      .replace(/\u00AD/g, '')
      // Remove zero-width characters
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      // NBSP to regular space
      .replace(/\u00A0/g, ' ')
      // Normalize dash variants to regular hyphen
      .replace(/[–—−]/g, '-')
      // Normalize fancy quotes to regular quotes
      .replace(/[„""]/g, '"')
      .replace(/['']/g, "'")
      // Collapse multiple spaces (but not newlines)
      .replace(/ +/g, ' ')
      // Trim trailing spaces from each line
      .split('\n')
      .map((line) => line.trimEnd())
      .join('\n')
  );
}

/**
 * Strip markdown formatting markers from text.
 *
 * Used when processing quote fragments before searching - the quotes may contain
 * markdown markers that need to be removed before matching against normalized text.
 * Note: The chapter blocks now preserve markdown markers for rendering.
 */
export function stripMarkdownMarkers(text: string): string {
  // Remove bold markers
  let result = text.replace(/\*\*/g, '');
  // Remove italic markers (single asterisks not part of bold)
  result = result.replace(/(?<!\*)\*(?!\*)/g, '');
  // Remove curly braces and square brackets (LLM sometimes uses {text} or [text] to indicate highlighted portions)
  // Also handles Unicode variants
  result = result.replace(/[{}｛｝﹛﹜❴❵[\]]/g, '');
  return result;
}

/**
 * Strip all formatting from quote text for clean display.
 *
 * Removes both markdown and HTML formatting:
 * - Headers (###, ##, #)
 * - Bold/italic markers
 * - List prefixes (-, *)
 * - HTML tags (table, tr, td, th, div, span, u, etc.)
 * - Curly braces used by LLM to indicate highlighted portions
 *
 * @param text - The text to clean
 * @param options.preserveLineBreaks - If true, <br> becomes newline. If false, <br> becomes space. Default: true
 * Used to clean up LLM-generated quotes that may include formatting.
 */
export function stripForDisplay(
  text: string,
  options?: { preserveLineBreaks?: boolean },
): string {
  const preserveLineBreaks = options?.preserveLineBreaks ?? true;

  // Convert <br> tags to newlines or spaces based on option
  let processed = text.replace(/<br\s*\/?>/gi, preserveLineBreaks ? '\n' : ' ');

  // Convert </td>, </th>, </tr> to newlines or spaces to separate table cells/rows
  processed = processed.replace(
    /<\/t[dhr]>/gi,
    preserveLineBreaks ? '\n' : ' ',
  );

  const lines = processed.split('\n');
  const cleanLines: string[] = [];

  for (const line of lines) {
    let cleaned = line;

    // Remove header markers at the start of lines (###, ##, #)
    cleaned = cleaned.replace(/^#{1,6}\s+/, '');

    // Remove list markers at the start of lines (- item, * item)
    cleaned = cleaned.replace(/^[-*]\s+/, '');

    // Remove bold markers
    cleaned = cleaned.replace(/\*\*/g, '');

    // Remove italic markers (single asterisks)
    cleaned = cleaned.replace(/(?<!\*)\*(?!\*)/g, '');

    // Remove all HTML tags (table, tr, td, th, div, span, u, etc.)
    cleaned = cleaned.replace(/<[^>]+>/g, '');

    // Remove curly braces and square brackets (LLM sometimes uses {text} or [text] to indicate highlighted portions)
    // Also handles Unicode variants
    cleaned = cleaned.replace(/[{}｛｝﹛﹜❴❵[\]]/g, '');

    const trimmed = cleaned.trim();
    if (trimmed) {
      cleanLines.push(trimmed);
    }
  }

  // Join with appropriate separator
  return cleanLines.join(preserveLineBreaks ? '\n' : ' ');
}
